import { useState, useCallback, useMemo, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ThemePickerModal } from '@/components/modals/ThemePickerModal';
import { ShortcutsModal } from '@/components/modals/ShortcutsModal';
import { renderReactComponent, destroyReactComponent } from '@/react-bridge';
import { App } from '@/services/app-compat';

// Value lookup: access lazily at usage sites via (window as any).HelpDeskType

// ── Types ─────────────────────────────────────────────────────────────

interface HomeScreenProps {
  moduleTitle: string;
  moduleShortTitle: string;
  helpDeskType: any;
}

interface CardDef {
  id: string;
  title: string;
  description: string;
  buttonText: string;
  icon: string;
  action: () => void;
}

// ── Component ─────────────────────────────────────────────────────────

export function HomeScreen({
  moduleTitle,
  moduleShortTitle,
  helpDeskType,
}: HomeScreenProps) {
  const { theme } = useTheme();
  const [themePickerOpen, setThemePickerOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  // Ensure company logo background is loaded (may not have fired if
  // user was already authenticated when the page loaded)
  useEffect(() => {
    App.preloadCompanyLogo();
  }, []);

  const goToGettingStarted = useCallback(() => {
    App.goToHelpDesk(helpDeskType);
  }, [helpDeskType]);

  const openThemePicker = useCallback(() => {
    setThemePickerOpen(true);
  }, []);

  const closeThemePicker = useCallback(() => {
    setThemePickerOpen(false);
  }, []);

  const openShortcuts = useCallback(() => {
    setShortcutsOpen(true);
  }, []);

  const closeShortcuts = useCallback(() => {
    setShortcutsOpen(false);
  }, []);

  const cards: CardDef[] = useMemo(
    () => [
      {
        id: 'gettingStarted',
        title: `New to ${moduleShortTitle}?`,
        description:
          'Welcome, please use the button below for a brief introduction to Orna.',
        buttonText: 'Getting Started',
        icon: 'fa fa-graduation-cap',
        action: goToGettingStarted,
      },
      {
        id: 'themePicker',
        title: 'Night/Day Mode:',
        description:
          "Use Orna's theme picker to set the color scheme that best suits you.",
        buttonText: 'Theme Picker',
        icon: 'fa fa-cogs',
        action: openThemePicker,
      },
      {
        id: 'shortcuts',
        title: 'Shortcut Keys:',
        description:
          "Use Orna's shortcuts for faster navigation / actions (advanced users).",
        buttonText: 'Shortcuts',
        icon: 'fa fa-cogs',
        action: openShortcuts,
      },
    ],
    [moduleShortTitle, goToGettingStarted, openThemePicker, openShortcuts]
  );

  return (
    <div className="homeSectionLogoBG">
      <div className={`home-screen ${theme}`.trim()}>
      <div className="home-screen__layout">
        <img
          src="/Content/Customize/img/Orna.png"
          alt="Orna"
          className="home-screen__logo"
        />
        <h1 className="home-screen__title">
          <b>{moduleTitle}</b>
        </h1>
        <div className="home-screen__cards">
          {cards.map((card) => (
            <div className="home-screen__card" key={card.id}>
              <div className="home-screen__card-body">
                <h4 className="home-screen__card-title">{card.title}</h4>
                <h5 className="home-screen__card-desc">{card.description}</h5>
                <div className="home-screen__card-action">
                  <button
                    type="button"
                    className="kstc-btn kstc-btn--primary home-screen__card-btn"
                    onClick={card.action}
                  >
                    {card.buttonText} <i className={card.icon}></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <ThemePickerModal
        open={themePickerOpen}
        theme={theme}
        onCancel={closeThemePicker}
      />
      <ShortcutsModal
        open={shortcutsOpen}
        theme={theme}
        onCancel={closeShortcuts}
      />
      </div>
    </div>
  );
}

// ── Imperative API (bridges legacy non-React code) ────────────────────

let homeScreenContainer: HTMLElement | null = null;

/**
 * Mount the HomeScreen React component into the `#homeScreen` element.
 * Replaces the Vue 2 `initHomeScreen()` function.
 */
function initHomeScreen(
  moduleTitle: string,
  moduleShortTitle: string,
  helpDeskType: any
): void {
  // Tear down any previous instance
  if (homeScreenContainer) {
    destroyReactComponent(homeScreenContainer);
    homeScreenContainer = null;
  }

  const el = document.getElementById('homeScreen');
  if (!el) return;

  homeScreenContainer = el;

  renderReactComponent(
    HomeScreen,
    { moduleTitle, moduleShortTitle, helpDeskType },
    homeScreenContainer
  );
}

/**
 * Mount a standalone ThemePickerModal via the React bridge.
 * Used by legacy code that needs to show the modal imperatively.
 */
function showThemePickerModal(): void {
  const container = document.createElement('div');
  document.body.appendChild(container);

  function ThemePickerWrapper() {
    const { theme } = useTheme();

    const handleClose = useCallback(() => {
      destroyReactComponent(container);
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
    }, []);

    return (
      <ThemePickerModal open={true} theme={theme} onCancel={handleClose} />
    );
  }

  renderReactComponent(ThemePickerWrapper, {}, container);
}

/**
 * Mount a standalone ShortcutsModal via the React bridge.
 * Used by legacy code that needs to show the modal imperatively.
 */
let shortcutsModalContainer: HTMLDivElement | null = null;

function showShortcutsModal(): void {
  if (shortcutsModalContainer) return;

  const container = document.createElement('div');
  document.body.appendChild(container);
  shortcutsModalContainer = container;

  function ShortcutsWrapper() {
    const { theme } = useTheme();

    const handleClose = useCallback(() => {
      destroyReactComponent(container);
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
      shortcutsModalContainer = null;
    }, []);

    return (
      <ShortcutsModal open={true} theme={theme} onCancel={handleClose} />
    );
  }

  renderReactComponent(ShortcutsWrapper, {}, container);
}

// ── Window registration ───────────────────────────────────────────────

(window as any).initHomeScreen = initHomeScreen;
(window as any).showThemePickerModal = showThemePickerModal;
(window as any).showShortcutsModal = showShortcutsModal;

// ── Exports ───────────────────────────────────────────────────────────

export { initHomeScreen, showThemePickerModal, showShortcutsModal };
